import { formatBytes } from '@jsonfmt/core';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentStore } from '@/features/document/documentStore';
import { ExamplesDialog } from '@/features/document/ExamplesDialog';
import { type ExampleId } from '@/features/document/examples';
import { loadExampleCommand } from '@/features/document/commands';
import { useDocument } from '@/features/document/useDocument';
import { EditorDocumentContext } from '@/features/editor/documentSource';
import { createEditorHandleSink, EditorHandleContext } from '@/features/editor/editorHandle';
import { EditorSlotsContext, type EditorSlots } from '@/features/editor/editorSlots';
import { applyFixAt } from '@/features/lint/commands';
import { buildProblemList, problemCursor, type ProblemRow } from '@/features/lint/problemList';
import { ProblemsPanel } from '@/features/lint/ProblemsPanel';
import { RepairLog } from '@/features/lint/RepairLog';
import { StatusPill } from '@/features/lint/StatusPill';
import { statusOf } from '@/features/lint/statusModel';
import { commands, toolbarCommands, views } from '@/features/registry';
import type { Command, FeatureApi, Notice, RepairReport, UiPort, ViewId } from '@/features/types';
import { createServices, isSuperseded } from '@/services';
import type { Services } from '@/services/types';
import { Banner } from '@/ui/components/Banner';
import { Button } from '@/ui/components/Button';
import { Drawer, type DrawerTab } from '@/ui/components/Drawer';
import { ErrorBoundary } from '@/ui/components/ErrorBoundary';
import { Toaster, type ToastLabels } from '@/ui/components/Toaster';
import { toastHub, type ToastHub } from '@/ui/notifications/toastHub';
import { AppShell } from '@/ui/shell/AppShell';
import { Header } from '@/ui/shell/Header';
import { SkipLink } from '@/ui/shell/SkipLink';
import { TabBar } from '@/ui/shell/TabBar';
import { Toolbar } from '@/ui/shell/Toolbar';
import type { ToolbarItem } from '@/ui/shell/types';
import { ViewHost } from '@/ui/shell/ViewHost';

import { useCommandContext, type Translate } from './commandContext';
import {
  DEFAULT_VIEW_ID,
  HARD_LIMIT_BYTES,
  LINT_OPTIONS,
  SOFT_LIMIT_BYTES,
  WORKER_THRESHOLD_BYTES,
  debounceMsFor,
} from './config';
import {
  bindingFor,
  buildKeymap,
  isApplePlatform,
  PLATFORM_KEYCAPS,
  renderShortcut,
  shortcutsOf,
} from './keymap';
import { Providers } from './Providers';
import { applyResult, runCommand, type RunCommandDeps } from './runCommand';
import { toShellView, toViewTab } from './viewProjection';

/**
 * THE COMPOSITION ROOT — docs/ARCHITECTURE.md §3, §4.9, §5.
 *
 * Everything the other six folders were forbidden from knowing, known here exactly once:
 * which services exist, which commands exist, which views exist, where the toast region is
 * mounted, what the size thresholds are, and whether this machine says `⌘` or `Ctrl`.
 *
 * ═══ THE THREE LAYERS OF THIS FILE ═══
 *
 *   App        owns the two long-lived objects — `Services` and `DocumentStore` — and mounts
 *              the providers. It renders no chrome and reads no translation.
 *   Workspace  the application proper: projects `commands` to a toolbar, `views` to a shell
 *              view, the report to a problems panel, and fills the editor's slots.
 *   Slots      the three regions the editor pane owns the LAYOUT of but not the CONTENT of.
 *
 * The split exists because `Workspace` uses `useTranslation`, `useServices` and `useTheme`,
 * all of which need a provider above them. A single component would have to mount the
 * providers and consume them in the same render, which React does not allow.
 *
 * ═══ ONE SEAM, FOUR READERS ═══
 *
 * `features/registry.ts` exports `commands` and `views`, and that array is read FOUR times
 * here: as toolbar buttons, as `buildKeymap`'s input, as the empty state's action row, and as
 * the blocked state's. That is the DRY property ADR-0014 is built around — a shortcut printed
 * on a button and the shortcut that fires it are the same string read from the same object,
 * so they cannot disagree — and it used to be true of only the first two.
 *
 * The other two hand-wrote what they should have read. `EmptyActions` printed
 * `renderShortcut('Mod-e', mac)` next to a button whose command it had just looked up by id,
 * and `BlockedActions` printed `'Mod-Backspace'` the same way — verbatim copies of
 * `features/document/commands.ts:228` and `:199` with nothing connecting them. Changing
 * `openExamplesCommand.shortcut` to `Mod-Shift-e` (measured: one line, five green gates,
 * 3 314 passing tests) left the toolbar and the keymap moving together and the empty state
 * still printing ⌘E for a key bound to nothing — on the first screen every user sees.
 *
 * So all four go through `toActionItem` below, and the shortcut always comes from
 * `shortcutsOf(command)`. `keymap.test.ts` asserts the remaining hazard, which is two commands
 * claiming one key; `App.test.tsx` asserts that no row prints a key the registry does not have.
 */

/**
 * The `id` of the diagnostics drawer, and therefore the value of the door's `aria-controls`.
 *
 * A constant and not a `useId`, because it is half of a relationship whose other half lives in
 * a different component: the door is in the header, the drawer is inside the editor pane, and
 * `e2e/a11y.spec.ts` asserts that following the attribute finds a real element. There is
 * exactly one of each on the page.
 */
const DIAGNOSTICS_ID = 'diagnostics';

/** Two beats of 600ms (ADR-0101 §8.3), plus a tick so the attribute outlives the animation. */
const PULSE_MS = 1300;

// ── the root ────────────────────────────────────────────────────────────────────────────────

export default function App(): ReactElement {
  /**
   * Built ONCE, in a lazy initialiser, and never rebuilt by a re-render.
   *
   * `createServices` opens a Worker and installs a `matchMedia` listener, and `DocumentStore`
   * owns a debounce timer; constructing either during render would leak one per render. A
   * lazy `useState` initialiser runs exactly once per mounted component even under React 19's
   * StrictMode double-render, which a `useMemo` does not promise.
   *
   * NEITHER IS DISPOSED ON UNMOUNT, deliberately. StrictMode mounts every component twice in
   * development, so a cleanup would tear down the analyzer that the second mount is still
   * using — and this component unmounts exactly once, when the page goes away, at which point
   * the browser reclaims the Worker anyway. The disposal paths exist and are unit-tested;
   * nothing in the application is in a position to call them correctly.
   */
  const [services] = useState<Services>(() =>
    createServices({ analyzer: { workerThresholdBytes: WORKER_THRESHOLD_BYTES } }),
  );

  const [store] = useState<DocumentStore>(
    () =>
      new DocumentStore({
        analyzer: services.analyzer,
        softLimitBytes: SOFT_LIMIT_BYTES,
        hardLimitBytes: HARD_LIMIT_BYTES,
        debounceMsFor,
        lintOptions: LINT_OPTIONS,
        // The real guard, injected rather than imported: `DocumentStore` lives in a DOM-free
        // program and may not reach the `@/services` barrel (gate 1/gate 2). A lint that a
        // newer keystroke evicted from the pending slot rejects with `reason: 'superseded'`,
        // and swallowing exactly that — and nothing else — is this callback's whole job.
        isSuperseded,
        onLintError: (error: unknown) => {
          // A lint that failed for a real reason. No toast: the status pill is already showing
          // the neutral stale state, and a document that is merely un-analysed is not an error
          // the user can act on.
          console.error('lint failed', error);
        },
      }),
  );

  return (
    <Providers services={services}>
      <RootBoundary>
        <Workspace store={store} />
      </RootBoundary>
    </Providers>
  );
}

/**
 * The last line of defence. Inside the providers, because its own fallback is localised.
 *
 * There is no reporting SDK behind this and there will not be one (ADR-0023): the whole of
 * error reporting in this product is `console.error`, and the whole of recovery is a reload.
 */
function RootBoundary({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      detailsLabel={t('error.details')}
      fallback={() => (
        <div role="alert" style={{ margin: '0 auto', maxWidth: '40rem', padding: '2rem' }}>
          <h1>{t('error.app.title')}</h1>
          <p>{t('error.app.body')}</p>
          <Button
            label={t('error.reload')}
            onClick={() => {
              window.location.reload();
            }}
            variant="primary"
          />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

// ── the application ─────────────────────────────────────────────────────────────────────────

interface WorkspaceProps {
  readonly store: DocumentStore;
}

function Workspace({ store }: WorkspaceProps): ReactElement {
  const { t } = useTranslation();
  const doc = useDocument(store);

  const [examplesOpen, setExamplesOpen] = useState(false);
  const [repair, setRepair] = useState<RepairBanner | null>(null);
  /** The same record, kept past the banner's lifetime — see `onNotice` below, and §7.15. */
  const [repairLog, setRepairLog] = useState<RepairBanner | null>(null);

  /**
   * THE DIAGNOSTICS DRAWER — ADR-0101 §8.1.
   *
   * COLLAPSED ON EVERY LOAD, and never persisted. ADR-0023 rules out writing anything about a
   * document to storage, and "was your problems list open last time" is about a document; it is
   * also the wrong default twice over, because the first thing a user does is paste, and a
   * drawer that was open from a previous session would cover the paste they just made.
   *
   * ONE piece of state, not two booleans. `{ open, tab }` cannot express "closed on a tab that
   * no longer exists" as two independent updates racing each other: the door opens it ON a tab
   * (§8.2 picks which), and closing leaves the tab alone so reopening returns to where the user
   * was.
   */
  const [diagnostics, setDiagnostics] = useState<{
    readonly open: boolean;
    readonly tab: 'problems' | 'repairs';
  }>({ open: false, tab: 'problems' });

  /**
   * The two ends of the focus contract (§8.2). The door is in the header and the drawer is
   * inside the editor pane — three components apart, with no common ancestor that renders
   * either — so the refs are held HERE, which is the only place that knows about both.
   */
  const doorRef = useRef<HTMLButtonElement | null>(null);
  const tabRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);

  /**
   * WHICH VIEW IS ON SCREEN — the last wire of the extension seam ADR-0020 is built around.
   *
   * It was a constant, and with one view registered nothing on screen was wrong. The seam
   * was still broken: ARCHITECTURE §8.2 promises that adding the Tree view is "one or two
   * lines in `registry.ts`, zero edits in `ui/shell`", §8.3 lists `src/app/**` under what the
   * maintainer does NOT touch, and ADR-0020 enumerates "three one-line edits". Following
   * those instructions produced a tab strip with two real, focusable, clickable tabs, the
   * second of which did nothing: `const active = views[0]` and `onSelect={() => undefined}`.
   * Arrow keys were worse than a dead click — `TabBar.move()` moved DOM focus to a tab that
   * could never become selected, so a screen reader announced "tab 2 of 2" on a panel that
   * was not mounted.
   *
   * `ui/shell/ViewHost.test.tsx` did not catch it because its `StubShell` holds exactly this
   * state itself, under a comment reading "what `src/app/` will do, in eleven lines" — the
   * test substituted a correct composition root for the real one. The `registry.test.ts`
   * added alongside this change mounts the REAL `App` with a second descriptor instead.
   *
   * The fallback to `views[0]` is not defensive padding: `DEFAULT_VIEW_ID` names a view the
   * registry is free to stop registering, and a composition root that throws because a
   * constant went stale would be a worse failure than showing the first tab.
   */
  const [activeViewId, setActiveViewId] = useState<ViewId>(DEFAULT_VIEW_ID);

  /**
   * The editor's imperative handle, travelling upwards out of a lazily loaded panel.
   *
   * `useRef(createEditorHandleSink())` would call the factory on every render and throw the
   * result away; the null-coalescing assignment is the lazy form. The sink survives the
   * panel: a crash inside CodeMirror unregisters its handle and `UiPort` degrades to no-ops
   * rather than throwing at whoever pressed a key.
   */
  const sinkRef = useRef<ReturnType<typeof createEditorHandleSink> | null>(null);
  sinkRef.current ??= createEditorHandleSink();
  const sink = sinkRef.current;

  /** Decided once, here. Nothing below `src/app/` may ask which machine it is running on. */
  const mac = useMemo(isApplePlatform, []);

  const ui = useMemo<UiPort>(
    () => ({
      focusEditor: () => sink.current?.focus(),
      revealSpan: (span) => sink.current?.revealSpan(span),
      openDialog: () => {
        setExamplesOpen(true);
      },
      // Real state, read through a getter. `isAvailable: (ctx) => ctx.ui.activeViewId() ===
      // 'tree'` is the idiom ARCHITECTURE §8.1 and ADR-0020 §76 give for a view-scoped
      // command, and while this answered a constant every such command would have been
      // permanently unavailable — a toolbar button that silently never appears, with no error
      // anywhere to explain it.
      activeViewId: () => activeViewId,
      /*
       * F8's half of ADR-0101 §8.2. IDEMPOTENT ON PURPOSE: pressing it twelve times walks
       * twelve problems and opens the drawer once, because a step command that also TOGGLED
       * would close the list on the user halfway down it. The functional update returns the
       * same object when nothing changes, so React bails out of the re-render entirely.
       *
       * It never touches the tab beyond naming `problems`: F8 means "next problem", and
       * landing a user in the Repair log would answer a question they did not ask.
       */
      showProblems: () => {
        setDiagnostics((current) =>
          current.open && current.tab === 'problems' ? current : { open: true, tab: 'problems' },
        );
      },
    }),
    [sink, activeViewId],
  );

  const ctx = useCommandContext(ui);
  const translate = ctx.t;

  /**
   * The auto-repair banner of docs/DESIGN.md §6.5 watches the SAME notice the toast shows.
   *
   * Keyed on `Notice.key`, which `npm run i18n:check` gates, rather than on a toast id, which
   * is a private string. The banner is pinned to the document version the repair produced, so
   * the next edit retires it — a persistent banner describing a document that has moved on is
   * the stale-verdict failure ADR-0009 spends its whole argument on.
   */
  const onNotice = useCallback(
    (notice: Notice) => {
      if (!notice.key.startsWith('toast.repair.')) return;
      if (notice.repair === undefined) return;
      const memory = { report: notice.repair, version: store.getSnapshot().version };
      setRepair(memory);
      /*
       * THE SAME FACTS, KEPT LONGER — ADR-0101 §7.15.
       *
       * Two states out of one notice, and the duplication is the point. The BANNER is
       * transient by design (ADR-0026): it is pinned to the version the repair produced and the
       * next keystroke retires it, because a banner describing a document that has moved on is
       * the stale-verdict failure ADR-0009 spends its whole argument on. The LOG is not a claim
       * about the current document — it is a record of an edit that happened — so it survives
       * both the next keystroke and a dismissal of the banner, and is replaced only by the next
       * repair or by an undo that reverses it.
       *
       * It keeps the version anyway, because "Undo all" is a claim about the current document
       * and may only be offered while `EditorHandle.undo()` would still reverse THIS repair.
       */
      setRepairLog(memory);
    },
    [store],
  );

  /**
   * The toast store, with the platform keycap substituted into the finished sentence.
   *
   * `toast.clear.success` and `toast.error.clipboardRead` interpolate a `{{shortcut}}` that
   * `features/document/commands.ts` fills with BOTH spellings, because that file is compiled
   * without `navigator` and refuses to guess (gate 1). The composition root knows the machine,
   * so it narrows `'Ctrl+Z / ⌘Z'` to one keycap on the way past — the single substitution
   * point that file asks for by name, rather than a rule repeated at three call sites.
   */
  const hub = useMemo<ToastHub>(
    () => ({
      ...toastHub,
      show: (input) => toastHub.show({ ...input, message: resolveKeycaps(input.message, mac) }),
    }),
    [mac],
  );

  const runDeps = useMemo<RunCommandDeps>(
    () => ({
      hub,
      hardLimitBytes: HARD_LIMIT_BYTES,
      closeDialogs: () => {
        setExamplesOpen(false);
        /*
         * AND THEN PUT THE KEYBOARD BACK IN THE EDITOR — a reconciliation between two peers
         * that each behaved correctly on their own.
         *
         * `loadExampleCommand` calls `ctx.ui.focusEditor()` inside its own `run`, because
         * after loading an example the user should be able to start typing. `Dialog` restores
         * focus to the control that OPENED it when it closes, because that is what a modal
         * owes a keyboard user who dismissed it. Both are right; run in that order they
         * fight, and the dialog wins — the user lands back on "Load example" with a document
         * they cannot edit without reaching for the mouse.
         *
         * The command's intent is the one to honour, so the editor is re-focused AFTER the
         * close has finished. `requestAnimationFrame` rather than a bare call because the
         * restoration happens in `Dialog`'s own effect, which React runs after this state
         * update commits; a frame later is after both. `e2e/a11y.spec.ts` asserts the
         * outcome, so if the ordering ever changes the suite says so rather than a user
         * discovering it.
         */
        requestAnimationFrame(() => {
          sink.current?.focus();
        });
      },
      onNotice,
    }),
    [hub, onNotice, sink],
  );

  const run = useCallback(
    (command: Command) => {
      void runCommand(command, ctx, store, runDeps);
    },
    [ctx, store, runDeps],
  );

  /**
   * What a PANEL may do. The only channel from a lazily loaded view back into the application,
   * and every one of its writes goes through `applyResult` — the same four ordered steps a
   * command takes. A panel writing to the store directly would be a second ordering of the
   * same effects, which is how two orderings drift.
   */
  const api = useMemo<FeatureApi>(
    () => ({
      replaceDocument: (text, opts) =>
        applyResult(
          { document: { text, label: opts?.label ?? 'feature.replace' } },
          ctx,
          store,
          runDeps,
        ),
      applyEdits: (edits) => applyResult({ edits }, ctx, store, runDeps),
      revealSpan: (span, opts) => sink.current?.revealSpan(span, opts),
      notify: (notice) => applyResult({ notice }, ctx, store, runDeps),
      t: translate,
    }),
    [ctx, store, runDeps, sink, translate],
  );

  // The verdict, and the one thing the toolbar below needs from it. Computed here rather than
  // beside its own consumer further down because `statusOf` is pure and `doc` is all it reads.
  const status = statusOf(doc);

  /**
   * ── `23 lines · 412 B`, the desktop toolbar's one fact (ADR-0101 §7.6) ──────────────────
   *
   * It fills the gap between the transforms and "Load example" from 960px, which is where
   * `Toolbar.module.css` puts a `.spacer` and the mockup puts this. `Toolbar` already owns the
   * slot, its DOM position and its breakpoint; the composition root owns the sentence, because
   * `ui/**` does not translate (ADR-0003) and does not know a document from a hole.
   *
   * COUNTED, NOT PLURALISED BY HAND: `{{count}}` and the `_one` / `_other` pair, per ADR-0018.
   *
   * THE BAND GUARD IS A PERFORMANCE DECISION, not a cosmetic one. Counting newlines is a walk
   * of the whole text, and this recomputes on every keystroke where the lint is debounced
   * precisely so that walks do not. Past the soft limit the product has already decided that
   * per-keystroke work over the document is what it will not do (ADR-0009, §11) — so the tag
   * is a fact about a document small enough for the fact to be free. An empty buffer has the
   * empty state to describe it and does not need "1 line · 0 B" in the chrome as well.
   */
  const documentTag = useMemo<string | undefined>(() => {
    if (doc.size !== 'normal' || doc.text.length === 0) return undefined;

    let lines = 1;
    for (let at = doc.text.indexOf('\n'); at >= 0; at = doc.text.indexOf('\n', at + 1)) lines += 1;

    return translate('editor.documentTagShape', { count: lines, size: formatBytes(doc.bytes) });
  }, [doc.size, doc.text, doc.bytes, translate]);

  // ── the toolbar, projected from the registry ──────────────────────────────────────────────

  /**
   * ONE GRADIENT CONTROL PER SCREEN — ADR-0101 §1 rule 2, on the one screen that has two.
   *
   * Past the hard limit the editor is replaced by the blocked card, and that card's own CTA is
   * a hero: "Format without the editor" is the thing to press, and §1 nominates it by name as
   * one of Format's two stand-ins. The dock is still on screen above it with Format wearing
   * `--grad-citrus` too, so the blocked screen shipped with two identical citrus heroes
   * competing — the exact reading the rule exists to prevent, and the opposite of the mockup,
   * which draws Format quiet in the dock so the card's CTA is the only gradient on the page.
   *
   * Format stays ENABLED: the card's own band table says "Format, validate and download still
   * work", and `formatCommand.isEnabled` agrees. Only the emphasis moves. Lime because the
   * command keeps its meaning — "make it valid" — while giving up the gradient (§1's table).
   *
   * NOTHING HERE BRANCHES ON A COMMAND ID, for the same reason `toToolbarItem` does not: the
   * rule is about emphasis, so the test is the emphasis. Whichever item claims `primary` is
   * the one that has to stand down, and a future registry that moves the gradient to another
   * command needs no edit here.
   */
  const demoteHero = status.kind === 'blocked';

  const items = useMemo<readonly ToolbarItem[]>(
    () =>
      toolbarCommands.map((command) => {
        const item = toToolbarItem(command, ctx, doc, translate, mac, run);
        if (!demoteHero || item.variant !== 'primary') return item;
        return { ...item, variant: 'tinted', hue: 'lime' };
      }),
    [ctx, doc, translate, mac, run, demoteHero],
  );

  // ── the keyboard, projected from the SAME registry ────────────────────────────────────────

  const storeRef = useRef(store);
  storeRef.current = store;

  const appKeymap = useMemo(
    () => buildKeymap(commands, ctx, { ...runDeps, getStore: () => storeRef.current }),
    [ctx, runDeps],
  );

  /**
   * ── and the same keymap again, for when the editor does NOT have focus ──────────────────
   *
   * THE PROMISE THAT WAS BROKEN. Every toolbar button prints its own shortcut on its face and
   * says it again through `a11y.actionWithShortcut`. Those bindings reached CodeMirror's
   * keymap and nowhere else, so they worked only while the editor had focus — and clicking a
   * toolbar button leaves focus ON that button. Press Format with the mouse, then press ⌘⏎ to
   * do it again, and nothing happens, with the keycap ⌘⏎ printed on the control under the
   * cursor. Tab to any button and the whole keyboard is dead.
   *
   * ONE KEYMAP, TWO DISPATCHERS. This listener consumes the SAME `appKeymap` array the editor
   * installs, so the two cannot know different shortcuts; the only question is which of them
   * is live, and the answer is exactly one:
   *
   *   - the editor has focus  → CodeMirror's keymap runs it, and this listener stands down;
   *   - anything else focused → this listener runs it.
   *
   * `hasFocus()` is the discriminator and it is asked of the editor itself rather than
   * inferred from `event.target`, because CodeMirror's focus lives on a contenteditable
   * inside a shadow-free but deeply nested tree and `document.activeElement` is not reliable
   * across a `dispatch`. `binding.run()` returns `false` for an unavailable or inapplicable
   * command, and a `false` is left alone — Ctrl+Backspace in a text field must still delete a
   * word. ADR-0024 records the decision and the alternative that was rejected.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      // CodeMirror owns this keystroke. Running it here as well would fire every command twice.
      if (sink.current?.hasFocus() === true) return;
      // A modal owns the keyboard while it is open (Esc closes it, Tab is trapped); firing
      // `⌘E` from inside the examples dialog would re-open the dialog the user is standing in.
      if (examplesOpen) return;
      if (isTypingTarget(event.target)) return;

      const binding = bindingFor(appKeymap, event, mac);
      if (binding === undefined) return;
      if (!binding.run()) return;
      event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [appKeymap, mac, sink, examplesOpen]);

  // ── the problems panel ────────────────────────────────────────────────────────────────────

  const fresh = doc.reportVersion === doc.version ? doc.report : null;

  const rows = useMemo(
    // `pathOf` is deliberately absent: a JSON path needs the CST, and the worker payload
    // carries none (ADR-0010). Every row's `path` is therefore `undefined` and the row
    // degrades silently, by design, rather than the panel branching on where it came from.
    () => buildProblemList(fresh?.diagnostics ?? [], { catalog: ctx.catalog }),
    [fresh, ctx.catalog],
  );

  const selectedIndex = useSyncExternalStore(
    useCallback((listener: () => void) => problemCursor.subscribe(listener), []),
    () => problemCursor.getSnapshot(),
    () => problemCursor.getSnapshot(),
  );

  const onSelect = useCallback((row: ProblemRow) => {
    problemCursor.select(row.index);
  }, []);

  const onActivate = useCallback(
    (row: ProblemRow) => {
      problemCursor.select(row.index);
      /*
       * ON A PHONE THE REVEAL DOES NOT TAKE THE KEYBOARD — ADR-0101 §8.2.
       *
       * On a desktop, jumping to a problem and landing in the text is exactly right: the next
       * thing the user does is type the fix. On a touch device, focusing a contenteditable
       * raises the soft keyboard, which eats half the screen, shrinks the sheet to 40dvh and
       * covers the line the user was sent to look at — three consequences of a gesture that
       * asked only "show me where".
       *
       * `(pointer: coarse)` rather than a width query: the question is what the input device
       * does when something takes focus, not how wide the window is. Asked at the moment of the
       * tap, so a tablet with a keyboard attached mid-session answers correctly.
       */
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      sink.current?.revealSpan(row.span, coarse ? { focus: false } : undefined);
    },
    [sink],
  );

  /**
   * ── after a Fix, focus must not fall on `<body>` — and it does not ──────────────────────
   *
   * ADR-0101 §7.14 puts a Fix button on every fixable row, and pressing one can remove the row
   * it is on; a focused element that unmounts drops the keyboard to `<body>`, from where the
   * next Tab restarts at the top of the page. That hazard is REAL and it is answered, but not
   * here: `applyFixAt` always returns a `reveal` when it changes anything, `runCommand`'s step
   * 3 hands that to `UiPort.revealSpan`, and the reveal focuses the editor — synchronously,
   * before React commits the list without the row. The keyboard is already somewhere else by
   * the time the button goes.
   *
   * THIS IS A DEVIATION FROM §8.2, and a deliberate one. The table says focus moves to "the
   * row now at the same index, clamped". That row does not exist yet at the moment of the Fix:
   * the list is built from a FRESH report (ADR-0009), the lint is debounced, and for the tick
   * in between `rows` is empty. Honouring the table literally means three focus moves — body,
   * then the tab, then a row a debounce later — where the reveal makes one, into the text the
   * user is about to type in. ADR-0101 records the trade and `e2e/a11y.spec.ts` pins it, which
   * is what this file used to attempt with a fifteen-line effect that could never reach its
   * own outcome: the reveal had always claimed the focus by the time it ran.
   */
  const onApplyFix = useCallback(
    (row: ProblemRow) => {
      run(applyFixAt(row.index));
    },
    [run],
  );

  // ── the diagnostics drawer: opening, closing and focus (ADR-0101 §8.2) ────────────────────

  /**
   * Whether the NEXT open should move the keyboard into the drawer.
   *
   * The door does; F8 does not (§8.2's table). Both write the same state, so the difference
   * cannot live in the state — it is a property of the gesture, and this ref is how the gesture
   * tells the effect below what it was. State here would cause a second render for a fact that
   * is consumed and discarded in the same commit.
   */
  const focusTabOnOpen = useRef(false);

  const onToggleDoor = useCallback(() => {
    setDiagnostics((current) => {
      if (current.open) return { ...current, open: false };
      focusTabOnOpen.current = true;
      /*
       * WHICH TAB THE DOOR OPENS ON — §8.2's first row. Problems if there are any, because
       * that is what the pill the user just pressed was counting; otherwise the Repair log if
       * one exists, because on a valid document that is the only thing there is to read;
       * otherwise Problems, which says "No problems found." and is a real answer.
       */
      const tab = rows.length > 0 ? 'problems' : repairLog === null ? 'problems' : 'repairs';
      return { open: true, tab };
    });
  }, [rows.length, repairLog]);

  /**
   * Closing from INSIDE the drawer — the × and Escape. Focus goes back to the door, which is
   * the control that opened it and the only place from which it can be opened again.
   *
   * The fallback is the editor, for the case where the door is no longer rendered at all: the
   * document went empty or past the hard limit while the drawer was open, and §7.5's table says
   * there is no door in either state.
   */
  const closeDiagnostics = useCallback(() => {
    setDiagnostics((current) => (current.open ? { ...current, open: false } : current));
    if (doorRef.current !== null) doorRef.current.focus();
    else sink.current?.focus();
  }, [sink]);

  const onTabChange = useCallback((tab: string) => {
    setDiagnostics((current) => ({
      open: current.open,
      tab: tab === 'repairs' ? 'repairs' : 'problems',
    }));
  }, []);

  // The door's click lands the keyboard on the selected tab: the drawer is far from the door in
  // DOM order, and a keyboard user who opened a list should be standing in it (§8.2).
  useEffect(() => {
    if (!diagnostics.open) return;
    if (!focusTabOnOpen.current) return;
    focusTabOnOpen.current = false;
    tabRef.current?.focus();
  }, [diagnostics.open, diagnostics.tab]);

  /**
   * ── a list that is open has a current row ────────────────────────────────────────────────
   *
   * Every expanded frame of ADR-0101 draws row 1 as current: the hue wash, the 3px inset edge
   * and the path. What shipped was a flat list until the user clicked or arrowed into it, and
   * on the keyboard that is worse than untidy — §8.2 sends focus to the TAB, so a keyboard
   * user opening the drawer saw nothing highlighted anywhere in the region they had just been
   * put into, and had no way to tell which row "next problem" would step away from.
   *
   * The SHARED cursor and not a local display value, because `problemList.ts` says why it is
   * shared: this panel's `aria-current`, `F8` and the reveal are three consumers of one index,
   * and a fourth answer that only the panel knew would be the disagreement that module exists
   * to prevent. Seeding it means F8 then steps to the SECOND row, which is what "next" means
   * once a first one is current.
   *
   * It seeds and never moves: `>= 0` leaves a real selection alone, so re-opening the drawer
   * returns the user to the row they left, and the clamp after a Fix stays the Fix's business.
   * No focus moves and no span is revealed — a highlight is not a jump.
   */
  useEffect(() => {
    if (!diagnostics.open || diagnostics.tab !== 'problems') return;
    if (problemCursor.getSnapshot() >= 0) return;
    const first = rows[0];
    if (first === undefined) return;
    problemCursor.select(first.index);
  }, [diagnostics.open, diagnostics.tab, rows]);

  /**
   * ── Escape closes the drawer from OUTSIDE it too (§8.2) ─────────────────────────────────
   *
   * `Drawer.tsx` handles Escape on its own `<aside>`, which covers every way of getting INTO
   * the drawer — the door, the tabs, a row, a Fix. It does not cover the one way of opening it
   * that deliberately leaves the user somewhere else: F8 opens the drawer and keeps the
   * keyboard in the editor, which §8.2 asks for and which is right. The result was a state the
   * feature creates and offers no key out of — F8 again is `lint.next-problem`, not a toggle,
   * and Escape from the editor reached nothing, while the door advertises `aria-keyshortcuts
   * ="F8"` as though F8 governed the drawer. Tab still escaped, so it was never a trap; it was
   * a promise with no key behind it.
   *
   * FOCUS DOES NOT MOVE. `closeDiagnostics` sends the keyboard back to the door, because there
   * the user pressed the door to get in. Here they are standing in the text they jumped to, and
   * taking them to the header would undo the jump F8 just made for them.
   *
   * Two things own Escape ahead of this, and they are named rather than inferred: anything
   * inside the drawer (its own handler, which also returns focus to the door) and the examples
   * dialog, which is modal and whose Escape closes it. Everything else, while the drawer is
   * open, means the drawer.
   *
   * CAPTURE, and this is the one decision here worth arguing. `@codemirror/commands` binds
   * Escape to `simplifySelection`, and F8 leaves the revealed span SELECTED — so on the bubble
   * this listener never saw the key: CodeMirror collapsed the selection, consumed it, and to
   * the user nothing at all had happened. Escape's meaning to a person is "dismiss the thing
   * that just appeared", and the thing that just appeared is the drawer. So while the drawer
   * is open and the keyboard is outside it, Escape is the drawer's, and `stopPropagation`
   * makes that exclusive rather than doing both jobs at once. The listener is installed only
   * in that state and removed with it, so the editor's own Escape is untouched the rest of
   * the time.
   */
  useEffect(() => {
    if (!diagnostics.open) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (examplesOpen) return;
      const root = drawerRef.current;
      if (root !== null && event.target instanceof Node && root.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      setDiagnostics((current) => (current.open ? { ...current, open: false } : current));
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [diagnostics.open, examplesOpen]);

  /**
   * ── the two states that have no door, and therefore no open drawer (§7.5, §8.2) ─────────
   *
   * An empty buffer (the user pressed Clear, or deleted everything) and a document past the
   * hard limit. Both remove the control that opens the drawer, so an open drawer would be a
   * region with no way back to it — and in the blocked case it would be listing diagnostics
   * about a document that is no longer being analysed at all (ADR-0009).
   *
   * If the keyboard was inside when it went, it goes to the editor: the door it would normally
   * return to does not exist in either state.
   */
  const doorKind = status.kind;
  useEffect(() => {
    if (doorKind !== 'empty' && doorKind !== 'blocked') return;
    setDiagnostics((current) => (current.open ? { ...current, open: false } : current));

    const root = drawerRef.current;
    if (root !== null && root.contains(root.ownerDocument.activeElement)) sink.current?.focus();
  }, [doorKind, sink]);

  /**
   * ── the editor is told how much of itself is covered (§8.5) ─────────────────────────────
   *
   * The phone sheet sits ON the code. Without this, activating a row would scroll the flagged
   * line to the middle of the editor's BOX — behind the sheet — and the list would send the
   * user to a line it is itself hiding.
   *
   * `ResizeObserver` and not a height constant: the sheet is content-sized between one row and
   * `--sheet-max`, it animates, and the soft keyboard shrinks it again.
   *
   * WHICH LAYOUT WE ARE IN IS ASKED OF CSS, NOT OF A BREAKPOINT. From 960px the drawer is a
   * column beside the code and covers nothing, and the single declaration that makes that true
   * is `position: static` in `Drawer.module.css`. Reading it back is one source of truth for
   * "am I overlapping the code"; a `matchMedia('(min-width: 960px)')` here would be a second
   * copy of the breakpoint, and the kind that goes stale silently.
   */
  useEffect(() => {
    const el = drawerRef.current;
    if (el === null || !diagnostics.open) {
      sink.current?.setScrollInset(0);
      return undefined;
    }

    const report = (): void => {
      const overlays = el.ownerDocument.defaultView?.getComputedStyle(el).position === 'absolute';
      sink.current?.setScrollInset(overlays ? el.getBoundingClientRect().height : 0);
    };

    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => {
      observer.disconnect();
      sink.current?.setScrollInset(0);
    };
  }, [diagnostics.open, diagnostics.tab, sink]);

  // ── the slots the editor pane owns the layout of ──────────────────────────────────────────

  const toastLabels = useMemo<ToastLabels>(
    () => ({
      region: t('a11y.notifications'),
      dismiss: t('a11y.dismissNotification'),
      tone: {
        success: t('toast.severity.success'),
        error: t('toast.severity.error'),
        warning: t('toast.severity.warning'),
        info: t('toast.severity.note'),
      },
    }),
    [t],
  );

  /**
   * The drawer's tabs — ADR-0101 §7.15.
   *
   * The Repair log tab EXISTS ONLY WHEN THERE IS A LOG. A permanent tab that is empty half the
   * time is the "Problems 0" affordance §0 item 6 removed, one level up; and with no tab there
   * is no empty state to write, no key to translate and no screen to design for a list that
   * has nothing in it.
   *
   * The Problems badge counts the REPORT, not `rows.length`. With a filter on, the list is the
   * errors while the badge still has to admit that a warning is being hidden — the job the
   * panel's own count chips used to do before §7.14 removed its head.
   */
  const diagnosticsTabs = useMemo<readonly DrawerTab[]>(() => {
    const errors = fresh?.counts.errors ?? 0;
    const warnings = fresh?.counts.warnings ?? 0;
    const total = errors + warnings + (fresh?.counts.infos ?? 0);

    const problems: DrawerTab = {
      id: 'problems',
      label: t('editor.problems.title'),
      // The glyph is the verdict: an alert while anything is outstanding, a check when nothing
      // is. Severity still travels as shape AND word AND tone (DESIGN §6.4) — the word is the
      // tab's own label and the tone is the badge beside it.
      icon: total > 0 ? 'error' : 'check',
      count: {
        value: total,
        tone: errors > 0 ? 'danger' : warnings > 0 ? 'warn' : 'quiet',
      },
    };

    if (repairLog === null) return [problems];

    return [
      problems,
      {
        id: 'repairs',
        label: t('editor.repairLog.title'),
        icon: 'spark',
        count: { value: repairLog.report.entries.length, tone: 'repair' },
      },
    ];
  }, [fresh, repairLog, t]);

  const showRepair = repair !== null && doc.version <= repair.version;
  /** "Undo all" may only be offered while `EditorHandle.undo()` would reverse THIS repair. */
  const canUndoRepair = repairLog !== null && doc.version <= repairLog.version;

  /**
   * ── the door pulses when it gets worse (ADR-0101 §8.3) ──────────────────────────────────
   *
   * Two beats of the hue glow when the ERROR COUNT INCREASES between two fresh reports. Not an
   * auto-open, not a toast, not a sound: the drawer stays shut and the control the user can
   * already see simply moves once, in the corner of their eye.
   *
   * Three guards, and each is a case the design names:
   *   · `fresh === null` is a stale report about different bytes; it is not a comparison, and
   *     the previous count is kept so the NEXT fresh report is compared with the last real one;
   *   · a null previous count is the first report of the session — there is no "increase" from
   *     nothing, and pulsing at the user the moment they paste would be an accusation;
   *   · while the repair banner is up, the banner is already speaking. §8.3's wording is "it
   *     does not pulse on the first report after a paste or load; the toast or banner speaks
   *     then", and the banner's presence is the signal this layer actually has. A paste that
   *     needed no repair raises no banner and is not otherwise distinguishable from typing
   *     here — `DocumentSnapshot` carries no provenance — so that narrower case is NOT
   *     suppressed. Reported as a deviation rather than papered over.
   *
   * `prefers-reduced-motion` needs nothing here: `base.css` collapses every animation, so the
   * attribute goes on and nothing moves.
   */
  const [pulse, setPulse] = useState(false);
  const lastErrors = useRef<number | null>(null);
  const bannerSpeaks = useRef(false);
  bannerSpeaks.current = showRepair;

  useEffect(() => {
    if (fresh === null) return undefined;

    const errors = fresh.counts.errors;
    const previous = lastErrors.current;
    lastErrors.current = errors;

    if (previous === null || errors <= previous) return undefined;
    if (bannerSpeaks.current) return undefined;

    setPulse(true);
    const timer = window.setTimeout(() => {
      setPulse(false);
    }, PULSE_MS);
    return () => {
      window.clearTimeout(timer);
      setPulse(false);
    };
  }, [fresh]);

  const slots = useMemo<EditorSlots>(
    () => ({
      problems: (
        <Drawer
          activeTab={diagnostics.tab}
          closeLabel={t('a11y.hideProblems')}
          id={DIAGNOSTICS_ID}
          label={t('a11y.diagnostics')}
          onClose={closeDiagnostics}
          onTabChange={onTabChange}
          open={diagnostics.open}
          ref={drawerRef}
          tabRef={tabRef}
          tabs={diagnosticsTabs}
        >
          {diagnostics.tab === 'repairs' && repairLog !== null ? (
            <RepairLog
              report={repairLog.report}
              undoKeycap={renderShortcut('Mod-z', mac)}
              onUndoAll={
                canUndoRepair
                  ? () => {
                      // The same call the banner's Undo makes: one repair is one history step
                      // (ADR-0006), so "all" is a single ⌘Z. Both records go when it lands —
                      // the log describes edits that no longer exist.
                      if (sink.current?.undo() === true) {
                        setRepair(null);
                        setRepairLog(null);
                      }
                    }
                  : undefined
              }
            />
          ) : (
            <ProblemsPanel
              rows={rows}
              selectedIndex={selectedIndex}
              onSelect={onSelect}
              onActivate={onActivate}
              onApplyFix={onApplyFix}
            />
          )}
        </Drawer>
      ),
      // Inside the pane, never at the root: on a phone the toast docks to the bottom edge of
      // the PANE and overlays the code, under a `--bg` fade drawn against the code itself
      // (docs/DESIGN.md §8, ADR-0101 §7.10). Both halves need the pane to be the positioned
      // ancestor, and at the root neither would work. The rule this used to cite — turning the
      // region `static` at ≤640px so a toast DISPLACED the code — is the one §7.10 overturned:
      // a phone already gives up the code twice, to the dock and to the repair banner, and a
      // third reflow that arrives for two seconds and leaves moves the document under the
      // finger of the person reading it.
      notifications: <Toaster labels={toastLabels} />,
      repair: showRepair ? (
        <RepairBannerView
          report={repair.report}
          onUndo={() => {
            if (sink.current?.undo() === true) setRepair(null);
          }}
          onDismiss={() => {
            setRepair(null);
          }}
          mac={mac}
        />
      ) : undefined,
      emptyActions: <EmptyActions ctx={ctx} doc={doc} run={run} t={translate} mac={mac} />,
      hintKeys: hintKeysOf(mac),
      blockedActions: (
        <BlockedActions ctx={ctx} doc={doc} run={run} t={translate} mac={mac} store={store} />
      ),
      appKeymap,
      // Repair runs synchronously on the main thread inside the paste transaction, so the cap
      // is "how much text may we afford to rewrite in one frame" — the same number above which
      // analysis moves to a Worker.
      pasteRepairMaxBytes: WORKER_THRESHOLD_BYTES,
      limits: {
        softBytes: SOFT_LIMIT_BYTES,
        hardBytes: HARD_LIMIT_BYTES,
        largeDebounceMs: debounceMsFor(SOFT_LIMIT_BYTES),
      },
    }),
    [
      rows,
      selectedIndex,
      onSelect,
      onActivate,
      onApplyFix,
      diagnostics,
      diagnosticsTabs,
      closeDiagnostics,
      onTabChange,
      repairLog,
      canUndoRepair,
      toastLabels,
      showRepair,
      repair,
      t,
      translate,
      ctx,
      doc,
      store,
      run,
      mac,
      sink,
      appKeymap,
    ],
  );

  // ── the view ──────────────────────────────────────────────────────────────────────────────

  const active = views.find((view) => view.id === activeViewId) ?? views[0];
  if (active === undefined) throw new Error('features/registry.ts registers no views.');

  const shellView = useMemo(() => toShellView(active, doc, api), [active, doc, api]);
  const tabs = useMemo(() => views.map((view) => toViewTab(view, translate)), [translate]);

  return (
    <EditorDocumentContext value={store}>
      <EditorHandleContext value={sink}>
        <EditorSlotsContext value={slots}>
          <AppShell
            skipLink={<SkipLink label={t('action.skipToEditor')} />}
            header={
              <Header
                productName={t('meta.productName')}
                privacyLabel={t('meta.privacy')}
                themeLabels={{
                  toLight: t('action.themeToLight'),
                  toDark: t('action.themeToDark'),
                  toSystem: t('action.themeToSystem'),
                }}
                status={
                  <StatusPill
                    model={status}
                    pulse={pulse}
                    /*
                      The pill IS the toggle (ADR-0101 §0 item 3). It suppresses the door
                      itself for the empty and blocked states — see `StatusPill.tsx` — so the
                      shell hands it one unconditionally and does not branch on the verdict.
                    */
                    door={{
                      expanded: diagnostics.open,
                      controls: DIAGNOSTICS_ID,
                      label: t('a11y.problemsDoor'),
                      onToggle: onToggleDoor,
                      ref: doorRef,
                    }}
                  />
                }
              />
            }
            toolbar={
              <Toolbar
                items={items}
                label={t('a11y.toolbar')}
                {...(documentTag === undefined ? {} : { documentTag })}
              />
            }
            /*
              Unconditionally. `TabBar` returns null while one view is registered, and
              `AppShell` pins `<main>` to the last grid row so the layout does not care —
              the rule "a strip appears at two views" lives in exactly one place.
            */
            tabs={
              <TabBar
                views={tabs}
                activeId={active.id}
                onSelect={setActiveViewId}
                label={t('view.editor.label')}
              />
            }
            mainLabel={t('a11y.editor')}
          >
            <ViewHost
              view={shellView}
              // `true` with no strip on screen is an ARIA lie; `false` with one loses the
              // tab↔panel association. Derived from the same list `TabBar` hides itself on.
              tabbed={views.length > 1}
              resetKeys={[doc.version]}
              detailsLabel={t('error.details')}
              fallback={(boundary) => (
                <div role="alert" style={{ padding: '1rem' }}>
                  <strong>{t('error.editor.title')}</strong>
                  <p>{t('error.editor.body')}</p>
                  <Button label={t('error.retry')} onClick={boundary.reset} />
                </div>
              )}
            />
          </AppShell>

          {/*
            A sibling of the shell, not a grid item: `<dialog>` opened with `showModal()`
            renders in the browser's top layer and has no business being laid out.
          */}
          <ExamplesDialog
            open={examplesOpen}
            undoKeycap={renderShortcut('Mod-z', mac)}
            onClose={() => {
              setExamplesOpen(false);
            }}
            onSelect={(id: ExampleId) => {
              run(loadExampleCommand(id));
            }}
          />
        </EditorSlotsContext>
      </EditorHandleContext>
    </EditorDocumentContext>
  );
}

// ── projections ─────────────────────────────────────────────────────────────────────────────

interface RepairBanner {
  /** The facts, from `Notice.repair` — the same repair the toast described in prose. */
  readonly report: RepairReport;
  /** The document version the repair produced. A later version retires the banner. */
  readonly version: number;
}

/**
 * `Command` → the props a `<Button>` needs, with the shortcut read from the command.
 *
 * The one projection behind all four readers of the registry. `toToolbarItem` builds a
 * `ToolbarItem` from it (the shell speaks that shape, not `Command`); the empty and blocked
 * rows render a `<Button>` from it directly, because those screens are not a toolbar and
 * `role="toolbar"`'s arrow-key semantics would be wrong on them.
 *
 * NOTHING HERE BRANCHES ON A COMMAND ID. It used to, twice — `command.id ===
 * 'document.clear' ? 'danger' : …` and an `align: 'end'` for `document.open-examples` — so a
 * command's appearance was described half in `registry.ts` and half here, with nothing keeping
 * the two in step. `Command.variant`, `Command.hue` and `Command.align` now carry all of it;
 * the registry's own claim that placement is "stated once, here, rather than reconstructed in
 * `src/app/App.tsx`" is true of the look as well.
 *
 * `labelKey` overrides the command's own, and that is by design rather than by accident:
 * docs/DESIGN.md §6.8 prescribes "Paste from clipboard" where the toolbar says "Paste", and
 * §6.9 prescribes "Clear buffer" where the toolbar says "Clear", because a lone button on an
 * empty screen has no neighbours to take its meaning from. The SHORTCUT is what must not be
 * overridden, and before this helper existed it was the one thing those call sites wrote out
 * by hand.
 */
interface ActionItem {
  readonly label: string;
  readonly icon: Command['icon'];
  readonly variant: NonNullable<Command['variant']>;
  readonly hue?: NonNullable<Command['hue']>;
  /** Already resolved from `Command.compactLabelKey`; absent when the command has none. */
  readonly compactLabel?: string;
  /** Already evaluated against this snapshot. Absent below 1, so no pill is drawn. */
  readonly badge?: number;
  readonly shortcutHint?: string;
  readonly available: boolean;
  readonly disabled: boolean;
  readonly onInvoke: () => void;
}

function toActionItem(
  command: Command,
  ctx: Parameters<typeof runCommand>[1],
  doc: Parameters<NonNullable<Command['isEnabled']>>[0],
  t: Translate,
  mac: boolean,
  run: (command: Command) => void,
  labelKey?: string,
): ActionItem {
  const [first] = shortcutsOf(command);
  const badge = command.badge?.(doc) ?? 0;

  return {
    label: t(labelKey ?? command.labelKey),
    icon: command.icon,
    variant: command.variant ?? 'tinted',
    ...(command.hue === undefined ? {} : { hue: command.hue }),
    // Dropped when the caller overrides the label: "Paste from clipboard" on the empty screen
    // has no neighbours to take its meaning from, and a compact form of a DIFFERENT word is
    // not a shorter version of this one.
    ...(command.compactLabelKey === undefined || labelKey !== undefined
      ? {}
      : { compactLabel: t(command.compactLabelKey) }),
    ...(badge > 0 ? { badge } : {}),
    ...(first === undefined ? {} : { shortcutHint: renderShortcut(first, mac) }),
    available: command.isAvailable?.(ctx) ?? true,
    disabled: command.isEnabled?.(doc) === false,
    onInvoke: () => {
      run(command);
    },
  };
}

/** `commands.find`, with the id checked against the registry rather than assumed. */
function commandById(id: string): Command | undefined {
  return commands.find((command) => command.id === id);
}

/**
 * `Command` → `ToolbarItem`: the projection `src/ui/shell/types.ts` describes.
 *
 * Every decision the shell is not allowed to make is made here — the label is resolved, the
 * shortcut is rendered for THIS platform, availability is collapsed to a boolean, and `run` is
 * closed over into a zero-argument callback.
 */
function toToolbarItem(
  command: Command,
  ctx: Parameters<typeof runCommand>[1],
  doc: Parameters<NonNullable<Command['isEnabled']>>[0],
  t: Translate,
  mac: boolean,
  run: (command: Command) => void,
): ToolbarItem {
  const action = toActionItem(command, ctx, doc, t, mac, run);
  const { label, shortcutHint: shortcut } = action;

  return {
    id: command.id,
    label,
    icon: action.icon,
    group: command.group,
    variant: action.variant,
    ...(action.hue === undefined ? {} : { hue: action.hue }),
    ...(action.compactLabel === undefined ? {} : { compactLabel: action.compactLabel }),
    ...(action.badge === undefined ? {} : { badge: action.badge }),
    ...(command.align === undefined ? {} : { align: command.align }),
    ...(shortcut === undefined
      ? { title: label }
      : {
          shortcutHint: shortcut,
          // Never a label and a keycap concatenated in JSX: a translator cannot reorder that.
          title: t('a11y.titleWithShortcut', { label, shortcut }),
          ariaLabel: t('a11y.actionWithShortcut', { label, shortcut }),
        }),
    available: action.available,
    disabled: action.disabled,
    onInvoke: action.onInvoke,
  };
}

/**
 * True for an element that owns the keystroke itself: a form field, or any other editable
 * host on the page.
 *
 * The editor is NOT identified this way — `EditorHandle.hasFocus()` answers for it, which is
 * both cheaper and correct across a CodeMirror `dispatch`. This covers the rest: a filter box
 * or a rename field added later must not have ⌘E stolen out from under it, and that should be
 * true by default rather than by remembering to add a guard.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Substitutes the single correct keycap into a toast sentence.
 *
 * `features/document/commands.ts` prints BOTH spellings (`'Ctrl+Z / ⌘Z'`) because it is
 * compiled without `navigator` (gate 1) and refuses to guess. The composition root knows, so
 * it narrows them — see `PLATFORM_KEYCAPS`. This is the substitution that file asks for by
 * name; the alternative was a `{{shortcut}}` placeholder resolved in three places.
 */
export function resolveKeycaps(text: string, mac: boolean): string {
  let out = text;
  for (const [both, [onMac, onPc]] of Object.entries(PLATFORM_KEYCAPS)) {
    out = out.split(both).join(mac ? onMac : onPc);
  }
  return out;
}

// ── the two action rows the editor's own screens ask for ────────────────────────────────────

interface ActionRowProps {
  readonly ctx: Parameters<typeof runCommand>[1];
  readonly doc: Parameters<NonNullable<Command['isEnabled']>>[0];
  readonly run: (command: Command) => void;
  readonly t: Translate;
  readonly mac: boolean;
}

/** One `<Button>` for one registered command, or nothing when the command is unavailable. */
function ActionButton({
  id,
  labelKey,
  variant,
  keycap = true,
  ...props
}: ActionRowProps & {
  readonly id: string;
  readonly labelKey: string;
  /**
   * Overrides the command's own emphasis for THIS screen. ADR-0101 §1 rule 2 allows exactly
   * one gradient control per screen, and which command wears it depends on where the user is:
   * Format owns it in the toolbar, but on the empty screen Format is disabled and the hero is
   * whichever control gets a document into the buffer.
   */
  readonly variant?: 'primary' | 'tinted';
  /**
   * `false` drops the printed keycap — not the shortcut, which still fires. §6.2's rule is
   * that a surface prints shortcuts when it is the user's only channel to them, and a screen
   * that is showing the dock two rows higher is not that surface: the blocked card's CTAs name
   * the same two commands the dock has already labelled `⌘⏎` and `⌘⌫`, and from 1120px the
   * duplicate keycaps widen three buttons past a card capped at 62ch until "Clear buffer"
   * drops to a second row. The mockup draws one row and no keycaps on these three.
   */
  readonly keycap?: boolean;
}): ReactElement | null {
  const command = commandById(id);
  if (command === undefined) return null;

  const item = toActionItem(command, props.ctx, props.doc, props.t, props.mac, props.run, labelKey);
  if (!item.available) return null;

  return (
    <Button
      label={item.label}
      icon={item.icon}
      variant={variant ?? item.variant}
      {...(item.hue === undefined ? {} : { hue: item.hue })}
      {...(item.shortcutHint === undefined || !keycap ? {} : { shortcutHint: item.shortcutHint })}
      onClick={item.onInvoke}
    />
  );
}

/**
 * docs/DESIGN.md §6.8 — the empty state offers the two ways to get a document in.
 *
 * "Paste from clipboard" is the HERO here (ADR-0101 §7.1). In the toolbar it is a peri tinted
 * button and Format is the gradient, because that is the order of the work; on this screen
 * Format is disabled — there is nothing to format — and a screen whose only emphasised control
 * is greyed out has no emphasised control at all. The command keeps its own `variant` for
 * every other surface; only this one overrides it.
 */
function EmptyActions(props: ActionRowProps): ReactElement {
  return (
    <>
      <ActionButton
        {...props}
        id="document.paste"
        labelKey="action.pasteFromClipboard"
        variant="primary"
      />
      {/* `editor.empty.loadExample` and not `action.loadExample`: the dock's pair is
          "Example" / "Load example", a control naming its own destination in a row of six
          other controls, and this is a sentence-shaped CTA under a heading. ADR-0101 §7.12's
          empty frames read "Load an example" — the same reason §6.8 gives for "Paste from
          clipboard" over "Paste" two lines above. */}
      <ActionButton {...props} id="document.open-examples" labelKey="editor.empty.loadExample" />
    </>
  );
}

/**
 * The keycap for each hint row of the empty state (docs/DESIGN.md §6.8), read from the
 * command that performs it.
 *
 * THE ROWS SHIPPED WITHOUT KEYCAPS, at every width. §6.8 asks for "four hint rows with
 * `<kbd>`" and the approved mockup has them; `EditorPanel.module.css` still styles `.hint`
 * with `justify-content: space-between` and, below 640px, flips it to a column to stack a
 * second element — layout written for a right-hand column that never arrived. §6.2 then
 * nominates the empty state as one of three surfaces that KEEP the shortcuts once the toolbar
 * sheds them below 1120px, so the compensating channel was itself the thing missing.
 *
 * Read from the registry, never written out: `⌘⏎` on this screen and the key that formats the
 * document are now the same string from the same object, which is the property the rest of
 * this file exists to hold.
 */
function hintKeysOf(mac: boolean): NonNullable<EditorSlots['hintKeys']> {
  const keyOf = (id: string): string | undefined => {
    const command = commandById(id);
    const [first] = command === undefined ? [] : shortcutsOf(command);
    return first === undefined ? undefined : renderShortcut(first, mac);
  };

  return {
    ...spread('format', keyOf('beautify.format')),
    ...spread('compact', keyOf('beautify.minify')),
    ...spread('nextProblem', keyOf('lint.next-problem')),
  };
}

/** `exactOptionalPropertyTypes` makes an explicit `undefined` a different thing from absent. */
function spread<K extends string>(key: K, value: string | undefined): Partial<Record<K, string>> {
  return value === undefined ? {} : ({ [key]: value } as Record<K, string>);
}

/**
 * docs/DESIGN.md §6.9 — "exits, not a dead end".
 *
 * Clear stays live while the rest of the toolbar is disabled: `clearCommand.isEnabled` is true
 * for an over-limit buffer on purpose, and there is a test in `features/lint` pinning it.
 *
 * `Validate only` is the one control here that is NOT a command: it is the only path that
 * analyses a document past the hard limit, typing never schedules a lint at that size, and a
 * `Command` cannot express it because `CommandResult` has no "ask the store to lint" shape.
 * It is named rather than hidden.
 */
function BlockedActions(props: ActionRowProps & { readonly store: DocumentStore }): ReactElement {
  const { store, ...row } = props;

  return (
    <>
      <ActionButton
        {...row}
        id="beautify.format"
        keycap={false}
        labelKey="action.formatWithoutEditor"
      />
      <Button
        label={row.t('action.validateOnly')}
        icon="check"
        variant="tinted"
        hue="peri"
        onClick={() => {
          void store.lintNow().catch((error: unknown) => {
            console.error('validate failed', error);
          });
        }}
      />
      <ActionButton {...row} id="document.clear" keycap={false} labelKey="action.clearBuffer" />
    </>
  );
}

/**
 * docs/DESIGN.md §6.5 — the auto-repair banner, in full.
 *
 * WHAT SHIPPED WAS A BARE SENTENCE. §6.5 asks for four things and the banner rendered one of
 * them. Missing were: the chip row (`trailing commas ×3`) that makes the claim checkable, the
 * sentence naming the LIMIT of the repair — "a tool that silently edits somebody's bytes owes
 * them that sentence" is the design's own wording — and `Undo`, which §6.5 and ADR-0100 both
 * require to be "a peer of the message, not an item hidden in a toast". `Banner` has declared
 * `detail` and `actions` props for exactly these since it was written, and neither call site
 * passed them.
 *
 * Undo was not in the toast either, as it turned out: nothing in the product ever set
 * `Notice.action`, so all three of `Toast.tsx`'s action branch, `runCommand`'s mapping and
 * `toastHub`'s spread were unreachable. A user who dismissed the banner and did not know ⌘Z
 * had no way to reverse a rewrite they never asked for.
 *
 * THE HEADLINE EQUALS THE SUM OF THE CHIPS — §6.5 states that as an invariant asserted in
 * code, not as a note about wording, which is why the count is computed from `chips` here and
 * never passed in beside them. `App.test.tsx` asserts it.
 */
function RepairBannerView({
  report,
  onUndo,
  onDismiss,
  mac,
}: {
  readonly report: RepairReport;
  readonly onUndo: () => void;
  readonly onDismiss: () => void;
  readonly mac: boolean;
}): ReactElement {
  const { t } = useTranslation();

  const total = report.chips.reduce((sum, chip) => sum + chip.count, 0);

  return (
    <Banner
      // `repair`, not `warning`: ADR-0101 §1 gives amber to "changed but still valid" — a
      // warning diagnostic — and lemon to "the tool rewrote this for you". Borrowing the
      // warning tone said the wrong one of those two in the one colour both would use.
      tone="repair"
      label={t('editor.repair.label')}
      detail={report.chips.map((chip) =>
        t('editor.repair.chip', { label: chip.label, count: chip.count }),
      )}
      // The clause that says how complete the repair was. It is a separate prop rather than
      // one string with the headline because the two are drawn differently and at different
      // widths (ADR-0101 §7.8) — and because concatenating them here would put the space
      // between two sentences in the composition root, where no translator can see it.
      note={
        report.residual > 0
          ? t('editor.repair.residual', { count: report.residual })
          : t('editor.repair.complete')
      }
      actions={
        // Lemon, the repair family: Undo belongs to the message about the repair it reverses.
        <Button
          label={t('action.undo')}
          icon="undo"
          size="sm"
          variant="tinted"
          hue="lemon"
          shortcutHint={renderShortcut('Mod-z', mac)}
          onClick={onUndo}
        />
      }
      dismiss={{ label: t('a11y.dismissRepairNotice'), onDismiss }}
    >
      {t('editor.repair.headline', { count: total })}
    </Banner>
  );
}
